import React, { useState, useEffect, useCallback, useRef } from 'react';
import ListenerMap from './ListenerMap';
import ListenerTable from './ListenerTable';

const ListenerDetails = React.memo(({ stream, viewMode, onViewModeChange, nginxAnalytics, ENFORCED_COUNT, icecastListeners }) => {
  const [nginxListeners, setNginxListeners] = useState([]);
  const [lastKnownListeners, setLastKnownListeners] = useState([]);
  const [lastDataTime, setLastDataTime] = useState(Date.now());
  // Removed loading state - data updates smoothly without overlays
  const [error, setError] = useState(null);
  
  // Use ref to track current listeners for stability check without causing re-renders
  const currentListenersRef = useRef([]);
  const lastDataTimeRef = useRef(Date.now());
  const lastKnownListenersRef = useRef([]);

  // Use passed nginx analytics data instead of fetching separately
  const processNginxListeners = useCallback(() => {
    try {
      setError(null);
      console.log('🔍 Processing listener data:', { nginxAnalytics, icecastListeners, stream });
      
      // PRIORITIZE NGINX ANALYTICS - Filter out proxy/localhost IPs from fallback data
      if (!nginxAnalytics?.listeners || nginxAnalytics.listeners.length === 0) {
        // Use real Icecast listener data but FILTER OUT localhost/proxy connections
        if (icecastListeners && icecastListeners.length > 0) {
          // Filter listeners for the selected stream if available
          const streamListeners = stream ? 
            icecastListeners.filter(listener => 
              listener.mount === stream.mount || 
              listener.mount?.includes(stream.mount?.replace('/radio', ''))
            ) : icecastListeners;
          
          // CRITICAL FIX: Filter out localhost/proxy IPs (127.0.0.1, ::1, localhost)
          // These are the nginx→icecast connections, not real listeners
          const realListeners = streamListeners.filter(listener => {
            const ip = listener.ip || '';
            if (!ip || ip === '127.0.0.1' || ip === '::1' || ip === 'localhost') return false;
            if (ip.startsWith('192.168.') || ip.startsWith('10.') || ip.startsWith('169.254.')) return false;
            // Only filter private 172.16-31.x.x range (RFC 1918), not all 172.x.x.x
            if (ip.startsWith('172.')) {
              const secondOctet = parseInt(ip.split('.')[1], 10);
              if (secondOctet >= 16 && secondOctet <= 31) return false;
            }
            return true;
          });
          
          console.log(`🎧 Using ${realListeners.length} real Icecast listeners (filtered from ${streamListeners.length} total, nginx unavailable)`);
          
          // Only use Icecast data if we have REAL listeners (not just proxy connections)
          if (realListeners.length > 0) {
            currentListenersRef.current = realListeners;
            lastKnownListenersRef.current = realListeners;
            lastDataTimeRef.current = Date.now();
            setNginxListeners(realListeners);
            setLastKnownListeners(realListeners);
            setLastDataTime(Date.now());
            console.log(`Icecast listener data updated - ${realListeners.length} real listeners`);
          } else {
            console.log('⚠️ Icecast data contains only proxy connections - waiting for nginx analytics');
            // Keep last known good data instead of showing proxy connections
            const timeSinceLastData = Date.now() - lastDataTimeRef.current;
            const staleDataThreshold = 30000; // 30 seconds
            
            if (timeSinceLastData < staleDataThreshold && lastKnownListenersRef.current.length > 0) {
              console.log(`⏳ Keeping last known ${lastKnownListenersRef.current.length} listeners (${Math.round(timeSinceLastData/1000)}s ago)`);
            } else {
              console.log('🔄 Clearing stale data - no real listeners');
              currentListenersRef.current = [];
              setNginxListeners([]);
              setLastKnownListeners([]);
            }
          }
        } else {
          // STABILITY: Don't clear immediately, use last known data if recent
          const timeSinceLastData = Date.now() - lastDataTimeRef.current;
          const staleDataThreshold = 30000; // 30 seconds
          
          if (timeSinceLastData < staleDataThreshold && lastKnownListenersRef.current.length > 0) {
            console.log(`⏳ No current data - keeping last known ${lastKnownListenersRef.current.length} listeners (${Math.round(timeSinceLastData/1000)}s ago)`);
            const currentListeners = currentListenersRef.current.map(l => `${l.ip}_${l.user_agent}`).sort().join('|');
            const knownListeners = lastKnownListenersRef.current.map(l => `${l.ip}_${l.user_agent}`).sort().join('|');
            
            if (currentListeners !== knownListeners) {
              currentListenersRef.current = lastKnownListenersRef.current;
              setNginxListeners(lastKnownListenersRef.current);
              console.log(`Using last known listener data`);
            }
          } else {
            console.log('🔄 No real listener data available from any source - clearing after timeout');
            if (currentListenersRef.current.length > 0) {
              currentListenersRef.current = [];
              setNginxListeners([]);
              setLastKnownListeners([]);
            }
          }
        }
        return;
      }
      
      // For nginx analytics, show all real listeners instead of filtering by stream
      // since nginx provides the real IP data we need for marketing campaigns
      const streamListeners = nginxAnalytics.listeners; // Show all real listeners
      
      console.log(`Showing all ${streamListeners.length} real listeners from nginx analytics`);
      console.log('Available streams in nginx data:', streamListeners.map(l => l.stream));
      
      // FIXED: Don't deduplicate by IP - show all listeners as they connect
      // Some listeners may have the same IP (NAT, corporate networks, etc.)
      // and we want to show the real connection count, not unique IPs
      const uniqueListeners = streamListeners;

      // Convert nginx data format to match ListenerTable expectations
      const formattedListeners = uniqueListeners.map((listener, index) => ({
        id: `nginx_${listener.ip}_${index}`,
        ip: listener.ip,
        location: {
          city: listener.location?.city || 'Unknown',
          region: listener.location?.region || 'Unknown',
          country: listener.location?.country || 'Unknown',
          lat: listener.location?.lat || 0,
          lon: listener.location?.lon || 0,
          isp: listener.location?.isp || 'Unknown'
        },
        user_agent: listener.userAgent,
        device: {
          browser: 'Unknown Browser', // nginx logs don't have parsed device info
          os: 'Unknown OS'
        },
        connected_seconds: 0, // nginx logs show timestamps, not duration
        timestamp: listener.timestamp
      }));
      
      // TEMPORARY: Disable flicker prevention to debug display issue
      console.log(`Processing ${formattedListeners.length} nginx listeners`);
      currentListenersRef.current = formattedListeners;
      lastKnownListenersRef.current = formattedListeners;
      lastDataTimeRef.current = Date.now();
      setNginxListeners(formattedListeners);
      setLastKnownListeners(formattedListeners);
      setLastDataTime(Date.now());
      console.log(`Found ${formattedListeners.length} real listeners (updated)`);
      
      // OLD STABILITY CHECK (temporarily disabled)
      // const currentListeners = currentListenersRef.current.map(l => `${l.ip}_${l.user_agent}`).sort().join('|');
      // const newListeners = formattedListeners.map(l => `${l.ip}_${l.user_agent}`).sort().join('|');
    } catch (error) {
      console.error('Error processing nginx analytics:', error);
      setError('Failed to process listener data');
    }
  }, [nginxAnalytics, ENFORCED_COUNT, icecastListeners, stream]);

  // Process nginx data when it changes - optimized to prevent unnecessary updates
  useEffect(() => {
    processNginxListeners();
  }, [processNginxListeners]);

  // Always prefer nginx real IP data when available, otherwise show icecast data
  const displayListeners = nginxListeners;

  // Safety check: Return early if critical props are missing
  if (!stream) {
    console.error('ListenerDetails: No stream provided');
    return <div>Error: No stream data available</div>;
  }

  return (
    <div className="listener-details">
      <div className="details-header">
        <h2 className="details-title">
          {nginxListeners.length > 0 ? 'Live Connected Listeners' : `Listeners for ${stream?.title || stream?.mount || 'Stream'}`}
        </h2>
        <div className="details-count">
          <span style={{ 
            fontSize: '1rem', 
            color: 'var(--accent-primary)',
            background: 'var(--bg-tertiary)',
            padding: '0.3rem 0.8rem',
            borderRadius: '15px',
            fontWeight: '600'
          }}>
            {ENFORCED_COUNT || 0} listeners
          </span>
          {nginxListeners.length > 0 && (
            <span style={{ 
              marginLeft: '0.5rem', 
              fontSize: '0.8rem', 
              color: 'var(--text-secondary)'
            }}>
              All Real IPs
            </span>
          )}
          {/* Smooth data updates - no loading indicator needed */}
        </div>
      </div>

      {/* View Toggle */}
      <div className="view-toggle">
        <button
          className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => onViewModeChange('list')}
        >
          List View
        </button>
        <button
          className={`toggle-btn ${viewMode === 'map' ? 'active' : ''}`}
          onClick={() => onViewModeChange('map')}
        >
          Map View
        </button>
      </div>

      {/* Content */}
      {error && (
        <div style={{
          textAlign: 'center',
          padding: '1rem',
          background: 'var(--error)',
          color: 'white',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          Error: {error}
        </div>
      )}
      
      {displayListeners.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem', 
          color: 'var(--text-secondary)' 
        }}>
          <h3>No listeners currently connected to this stream</h3>
          <p>Real-time listeners will appear here when they connect to {stream?.mount || 'this stream'}</p>
          <p style={{ fontSize: '0.9rem', marginTop: '1rem' }}>
            In production with open logs, you'll see real IP addresses and locations here
          </p>
        </div>
      ) : (
        <>
          {viewMode === 'map' ? (
            <ListenerMap listeners={displayListeners} />
          ) : (
            <ListenerTable listeners={displayListeners} enforcedCount={ENFORCED_COUNT} />
          )}
        </>
      )}
    </div>
  );
});

ListenerDetails.displayName = 'ListenerDetails';

export default ListenerDetails;